
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from "sonner";
import { supabase } from '../../supabase/client';
import { AuthContext, AuthContextType, AuthUser } from './AuthContext';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  // Check if user is already logged in on component mount and set up auth listener
  useEffect(() => {
    console.log("[AuthProvider] Setting up auth state listener");
    let mounted = true;
    
    // First set up the auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("[AuthProvider] Auth state changed:", event, "Session:", !!session);
        
        if (!mounted) return;
        
        if (session?.user) {
          // Get user profile data from the profiles table
          supabase
            .from('profiles')
            .select('first_name, last_name, phone, is_admin')
            .eq('id', session.user.id)
            .single()
            .then(({ data: profile, error: profileError }) => {
              if (!mounted) return;
              
              if (profileError && profileError.code !== 'PGRST116') {
                console.error("[AuthProvider] Error fetching profile:", profileError);
              }
              
              // Use profile data if available, otherwise fall back to user metadata
              const firstName = profile?.first_name || session.user.user_metadata?.first_name || '';
              const lastName = profile?.last_name || session.user.user_metadata?.last_name || '';
              const phone = profile?.phone || session.user.user_metadata?.phone || '';
              const isUserAdmin = profile?.is_admin || false;
              
              const authUser: AuthUser = {
                id: session.user.id,
                email: session.user.email || '',
                firstName,
                lastName,
                phone,
                isAdmin: isUserAdmin,
                profileId: session.user.id
              };
              
              console.log("[AuthProvider] User authenticated:", authUser);
              setUser(authUser);
              setIsAdmin(isUserAdmin);
              setLoading(false);
            });
        } else {
          // No user is signed in
          console.log("[AuthProvider] No session found, setting user to null");
          setUser(null);
          setIsAdmin(false);
          setLoading(false);
        }
      }
    );

    // Then check for existing session
    const initializeAuth = async () => {
      try {
        console.log("[AuthProvider] Checking for existing session");
        setLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!mounted) return;
        
        if (session?.user) {
          console.log("[AuthProvider] Found existing session for:", session.user.email);
          
          try {
            // Get profile data from profiles table
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('first_name, last_name, phone, is_admin')
              .eq('id', session.user.id)
              .single();
              
            if (profileError && profileError.code !== 'PGRST116') {
              console.error("[AuthProvider] Error fetching profile:", profileError);
            }
            
            // Use profile data if available, otherwise fall back to user metadata
            const firstName = profile?.first_name || session.user.user_metadata?.first_name || '';
            const lastName = profile?.last_name || session.user.user_metadata?.last_name || '';
            const phone = profile?.phone || session.user.user_metadata?.phone || '';
            const isUserAdmin = profile?.is_admin || false;
            
            const authUser: AuthUser = {
              id: session.user.id,
              email: session.user.email || '',
              firstName,
              lastName,
              phone,
              isAdmin: isUserAdmin,
              profileId: session.user.id
            };
            
            console.log("[AuthProvider] User profile loaded:", authUser);
            setUser(authUser);
            setIsAdmin(isUserAdmin);
          } catch (error) {
            console.error("[AuthProvider] Error fetching user data:", error);
            
            // Still create a minimal user object from auth data
            const authUser: AuthUser = {
              id: session.user.id,
              email: session.user.email || '',
              firstName: session.user.user_metadata?.first_name || '',
              lastName: session.user.user_metadata?.last_name || '',
              isAdmin: false,
              profileId: session.user.id
            };
            
            setUser(authUser);
            setIsAdmin(false);
          }
        } else {
          console.log("[AuthProvider] No existing session found");
          setUser(null);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error("[AuthProvider] Error checking session:", error);
        if (mounted) {
          setUser(null);
          setIsAdmin(false);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // Initialize auth
    initializeAuth().then(() => {
      console.log("[AuthProvider] Auth initialization completed");
    });

    // Clean up function
    return () => {
      console.log("[AuthProvider] Cleaning up auth listener");
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Sign in using Supabase auth
  const signIn = async (email: string, password: string) => {
    try {
      console.log("[AuthProvider] Signing in with:", email);
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        console.error("[AuthProvider] Authentication error:", error);
        return { success: false, error: error.message };
      }
      
      if (!data.user) {
        console.error("[AuthProvider] No user returned after successful login");
        return { success: false, error: "Authentication failed" };
      }
      
      console.log("[AuthProvider] Login successful for:", data.user.email);
      
      // User data will be set by the onAuthStateChange listener
      return { success: true };
    } catch (error: any) {
      console.error("[AuthProvider] Sign in error:", error);
      return { success: false, error: error.message || "An error occurred during sign in" };
    } finally {
      setLoading(false);
    }
  };

  // Sign out using Supabase auth
  const signOut = async () => {
    try {
      console.log("[AuthProvider] Signing out user", user?.email);
      setLoading(true);
      
      await supabase.auth.signOut();
      
      // Manually update state here as well
      setUser(null);
      setIsAdmin(false);
      
      // Navigate to home page
      console.log("[AuthProvider] Redirecting to home page after signout");
      navigate('/');
      toast("Signed out successfully");
    } catch (error: any) {
      console.error("[AuthProvider] Sign out error:", error);
      toast.error("Sign out failed", {
        description: error.message || "An error occurred during sign out",
      });
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    isAdmin,
    signIn,
    signOut
  };

  console.log("[AuthProvider] Current auth state:", { 
    user: user?.email || null, 
    isAdmin, 
    loading 
  });
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
